// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - REFERRAL MANAGER
// Handles referral code generation, tracking, completion, and tier bonuses.
// Storage: localStorage (pre-connect) → Supabase referrals + referral_stats tables
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from './supabaseClient';
// ─── Constants ─────────────────────────────────────────────────────────────

const REF_CODE_KEY = 'distil_referrer_code';
const REF_EXPIRY_KEY = 'distil_referrer_expiry';
const REF_WELCOMED_KEY = 'distil_referral_welcomed'; // Set after welcome banner shown
const REFERRAL_EXPIRY_DAYS = 30;
const QUALIFICATION_THRESHOLD = 500; // Purity points needed to qualify

export const REFERRAL_TIERS = [
  { name: 'Legend',     min: 100, bonus: 0.25, emoji: '👑' },
  { name: 'Ambassador', min: 50,  bonus: 0.20, emoji: '🌟' },
  { name: 'Evangelist', min: 25,  bonus: 0.15, emoji: '🔥' },
  { name: 'Recruiter',  min: 10,  bonus: 0.10, emoji: '⚡' },
  { name: 'Introducer', min: 1,   bonus: 0.05, emoji: '🤝' },
];

// ─── URL Param Capture ────────────────────────────────────────────────────

/**
 * Call once on app mount. Reads ?ref=CODE from URL and saves to localStorage.
 * First-click-wins: won't overwrite an existing unexpired code.
 */
export function captureReferralFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (!refCode) return;

    // Don't overwrite an existing valid referral
    const existing = getStoredReferralCode();
    if (existing) return;

    const expiry = Date.now() + REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(REF_CODE_KEY, refCode.toUpperCase());
    localStorage.setItem(REF_EXPIRY_KEY, expiry.toString());

    // Clean URL without reload so the ?ref= param doesn't persist visibly
    const clean = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', clean);
  } catch (e) {
    console.warn('[Referral] captureReferralFromUrl failed:', e);
  }
}

/**
 * Returns stored referral code if valid and not expired, else null.
 */
export function getStoredReferralCode() {
  try {
    const code = localStorage.getItem(REF_CODE_KEY);
    const expiry = localStorage.getItem(REF_EXPIRY_KEY);
    if (!code || !expiry) return null;
    if (Date.now() > parseInt(expiry, 10)) {
      clearStoredReferral();
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

function clearStoredReferral() {
  localStorage.removeItem(REF_CODE_KEY);
  localStorage.removeItem(REF_EXPIRY_KEY);
}

// ─── Code Generation ──────────────────────────────────────────────────────

/**
 * Get or create a referral code for this wallet.
 * Returns { code, isNew } — throws if wallet holds < 100 $DISTIL.
 * Pre-TGE: skips balance check (no $DISTIL in circulation yet).
 */
export async function getOrCreateReferralCode(walletAddress, skipBalanceCheck = true) {
  if (!walletAddress) throw new Error('No wallet address');

  // Check if code already exists
  const { data: existing } = await supabase
    .from('referral_stats')
    .select('referral_code')
    .eq('user_address', walletAddress)
    .single();

  if (existing?.referral_code) return { code: existing.referral_code, isNew: false };

  // Generate unique 6-char code
  const code = await generateUniqueCode();

  const { error } = await supabase
    .from('referral_stats')
    .insert({
      user_address: walletAddress,
      referral_code: code,
      total_referrals: 0,
      active_referrals: 0,
      total_earned: 0,
      tier: 'None',
    });

  if (error) throw error;
  return { code, isNew: true };
}

async function generateUniqueCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No confusable chars (0/O, 1/I)
  for (let attempt = 0; attempt < 10; attempt++) {
    let code = '';
    for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];

    const { data } = await supabase
      .from('referral_stats')
      .select('referral_code')
      .eq('referral_code', code)
      .maybeSingle();

    if (!data) return code;
  }
  throw new Error('Failed to generate unique referral code after 10 attempts');
}

// ─── Wallet Connect Hook ──────────────────────────────────────────────────

/**
 * Call when a wallet connects for the first time.
 * Creates a pending referral record if a valid referrer code is in localStorage.
 * Returns { showWelcome: bool, referrerAddress: string|null }
 */
export async function onReferralWalletConnect(walletAddress) {
  try {
    const refCode = getStoredReferralCode();
    if (!refCode) return { showWelcome: false, referrerAddress: null };

    // Don't show welcome banner again if already seen this session
    if (localStorage.getItem(REF_WELCOMED_KEY) === walletAddress) {
      return { showWelcome: false, referrerAddress: null };
    }

    // Look up referrer
    const { data: referrerData } = await supabase
      .from('referral_stats')
      .select('user_address')
      .eq('referral_code', refCode)
      .maybeSingle();

    if (!referrerData) return { showWelcome: false, referrerAddress: null };

    const referrerAddress = referrerData.user_address;

    // Anti-gaming: no self-referral
    if (referrerAddress === walletAddress) return { showWelcome: false, referrerAddress: null };

    // Check if already referred
    const { data: existing } = await supabase
      .from('referrals')
      .select('id, status')
      .eq('referred_address', walletAddress)
      .maybeSingle();

    if (existing) {
      // Already tracked — still show welcome if pending and not yet seen
      clearStoredReferral();
      if (existing.status === 'pending') {
        return { showWelcome: true, referrerAddress };
      }
      return { showWelcome: false, referrerAddress: null };
    }

    // Create pending referral
    await supabase.from('referrals').insert({
      referrer_address: referrerAddress,
      referred_address: walletAddress,
      referral_code: refCode,
      status: 'pending',
    });

    // Clear localStorage — now tracked in DB
    clearStoredReferral();

    return { showWelcome: true, referrerAddress };
  } catch (e) {
    console.error('[Referral] onReferralWalletConnect failed:', e);
    return { showWelcome: false, referrerAddress: null };
  }
}

// ─── Referral Completion ──────────────────────────────────────────────────

/**
 * Call after every purity score update.
 * Checks if referred user has hit threshold and completes the referral.
 * Returns { completed: bool, referrerAddress: string|null }
 */
export async function checkReferralCompletion(walletAddress, totalPurityScore) {
  if (!walletAddress || totalPurityScore < QUALIFICATION_THRESHOLD) {
    return { completed: false, referrerAddress: null };
  }

  try {
    const { data: pending } = await supabase
      .from('referrals')
      .select('id, referrer_address')
      .eq('referred_address', walletAddress)
      .eq('status', 'pending')
      .maybeSingle();

    if (!pending) return { completed: false, referrerAddress: null };

    // Mark completed
    await supabase
      .from('referrals')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        purity_at_completion: totalPurityScore,
      })
      .eq('id', pending.id);

    // Update referrer stats and tier
    await updateReferrerStats(pending.referrer_address);

    return { completed: true, referrerAddress: pending.referrer_address };
  } catch (e) {
    console.error('[Referral] checkReferralCompletion failed:', e);
    return { completed: false, referrerAddress: null };
  }
}

async function updateReferrerStats(referrerAddress) {
  try {
    const { data: completedReferrals } = await supabase
      .from('referrals')
      .select('id')
      .eq('referrer_address', referrerAddress)
      .eq('status', 'completed');

    const total = completedReferrals?.length || 0;
    const tier = getTierForCount(total);
    const totalEarned = total * 100;

    await supabase
      .from('referral_stats')
      .update({
        total_referrals: total,
        total_earned: totalEarned,
        tier,
        updated_at: new Date().toISOString(),
      })
      .eq('user_address', referrerAddress);
  } catch (e) {
    console.error('[Referral] updateReferrerStats failed:', e);
  }
}

// ─── Tier Utilities ───────────────────────────────────────────────────────

export function getTierForCount(count) {
  for (const tier of REFERRAL_TIERS) {
    if (count >= tier.min) return tier.name;
  }
  return 'None';
}

export function getTierInfo(tierName) {
  return REFERRAL_TIERS.find(t => t.name === tierName) || null;
}

export function getReferralBonusMultiplier(tierName) {
  const tier = getTierInfo(tierName);
  return tier ? tier.bonus : 0;
}

export function getNextReferralTier(currentCount) {
  const reversed = [...REFERRAL_TIERS].reverse();
  for (const tier of reversed) {
    if (currentCount < tier.min) return tier;
  }
  return null;
}

// ─── Stats Fetching ───────────────────────────────────────────────────────

/**
 * Fetch referral stats for the current user.
 * Returns { code, totalReferrals, activeReferrals, totalEarned, tier, recentReferrals[] }
 */
export async function getReferralStats(walletAddress) {
  if (!walletAddress) return null;
  try {
    const { data: stats } = await supabase
      .from('referral_stats')
      .select('*')
      .eq('user_address', walletAddress)
      .maybeSingle();

    if (!stats) return null;

    // Fetch recent referrals
    const { data: recent } = await supabase
      .from('referrals')
      .select('referred_address, status, created_at, completed_at')
      .eq('referrer_address', walletAddress)
      .order('created_at', { ascending: false })
      .limit(5);

    return {
      code: stats.referral_code,
      totalReferrals: stats.total_referrals || 0,
      activeReferrals: stats.active_referrals || 0,
      totalEarned: stats.total_earned || 0,
      tier: stats.tier || 'None',
      recentReferrals: recent || [],
    };
  } catch (e) {
    console.error('[Referral] getReferralStats failed:', e);
    return null;
  }
}

/**
 * Fetch referral leaderboard (top 50 by total_referrals).
 */
export async function getReferralLeaderboard(limit = 50) {
  try {
    const { data } = await supabase
      .from('referral_stats')
      .select('user_address, referral_code, total_referrals, tier')
      .gt('total_referrals', 0)
      .order('total_referrals', { ascending: false })
      .limit(limit);

    return data || [];
  } catch (e) {
    console.error('[Referral] getReferralLeaderboard failed:', e);
    return [];
  }
}

/**
 * Mark welcome banner as seen for this wallet (persists across refreshes).
 */
export function markWelcomeSeen(walletAddress) {
  localStorage.setItem(REF_WELCOMED_KEY, walletAddress);
}
